import { computed, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { Capacitor } from '@capacitor/core';
import { geolocationService } from '@/services/geolocationService';
import { useUserStore } from '@/stores/useUserStore';

export const MOBILE_NETWORK_RESTORED_EVENT = 'swapwish:mobile-network-restored';
export const MOBILE_LOCATION_RESTORED_EVENT = 'swapwish:mobile-location-restored';

const NETWORK_POLL_MS = 1000;
const LOCATION_POLL_MS = 1000;
// Quando il GPS è ATTIVO si continua comunque a monitorare (più lentamente, per
// non prosciugare la batteria con un fix al secondo) così da rilevare il GPS che
// viene SPENTO mentre l'app è aperta ("dopo tot tempo") e mostrare il banner.
const LOCATION_MONITOR_MS = 15000;
// Keep-alive posizione: mentre l'app è aperta col GPS attivo, il fix viene
// ri-salvato sul server a questo intervallo (in coda alla probe già in corso,
// nessuna lettura GPS aggiuntiva). Serve al fallback offline→indirizzo lato
// server: quel cron riporta all'indirizzo chi non salva fix GPS da >30 minuti,
// e senza keep-alive un utente ONLINE col GPS attivo ma fermo da tempo su
// una vista diversa dalla mappa (es. chat) verrebbe scambiato per offline.
const LOCATION_KEEPALIVE_MS = 10 * 60 * 1000;
const INTERNET_PROBE_URL = 'https://www.gstatic.com/generate_204';

function browserOnline(): boolean {
  if (typeof navigator === 'undefined') return true;
  return navigator.onLine !== false;
}

function emitWindowEvent(name: string): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(name));
}

async function hasInternetAccess(): Promise<boolean> {
  if (!browserOnline()) return false;
  if (typeof fetch === 'undefined' || typeof AbortController === 'undefined') return true;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 3000);
  try {
    await fetch(`${INTERNET_PROBE_URL}?t=${Date.now()}`, {
      method: 'GET',
      mode: 'no-cors',
      cache: 'no-store',
      signal: controller.signal,
    });
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timeoutId);
  }
}

export const useMobileHealthStore = defineStore('mobileHealth', () => {
  const userStore = useUserStore();

  const started = ref(false);
  const isOnline = ref(browserOnline());
  const gpsAvailable = ref(true);
  const checkingNetwork = ref(false);
  const checkingLocation = ref(false);
  // Banner informativo "uso indirizzo del profilo" chiuso dall'utente: resta
  // nascosto fino al prossimo accesso all'app. È solo in memoria, quindi un
  // riavvio dell'app (nuovo contesto JS → store ricreato) lo ripristina.
  const addressBannerDismissed = ref(false);

  let networkPollId: ReturnType<typeof setTimeout> | null = null;
  let locationPollId: ReturnType<typeof setTimeout> | null = null;
  // Inizializzato a "adesso": il primo salvataggio keep-alive avviene ~10 min
  // dopo l'avvio, non alla prima probe — all'avvio la posizione la salva già
  // il flusso mappa (resolveLocationForProducts), niente doppia scrittura.
  let lastGpsSaveAt = Date.now();

  const isMobileRuntime = computed(() => Capacitor.isNativePlatform());
  const hasSignedInUser = computed(() => !!userStore.user?.id);
  // Rete: il banner offline vale su OGNI runtime (anche web/PWA — prima era
  // solo nativo e gli utenti browser non avevano alcun indicatore). I banner
  // GPS restano nativi: la geolocalizzazione web ha un flusso diverso.
  const showNetworkBanner = computed(() => !isOnline.value);
  const showLocationBanner = computed(() =>
    isMobileRuntime.value
    && hasSignedInUser.value
    && !gpsAvailable.value
    && !userStore.hasDefaultAddress
  );
  // GPS spento MA indirizzo predefinito presente: la posizione usata è quella
  // dell'indirizzo del profilo. Banner informativo (non un avviso bloccante),
  // chiudibile dall'utente fino al prossimo accesso all'app.
  const showLocationAddressBanner = computed(() =>
    isMobileRuntime.value
    && hasSignedInUser.value
    && !gpsAvailable.value
    && userStore.hasDefaultAddress
    && !addressBannerDismissed.value
  );

  function dismissAddressBanner(): void {
    addressBannerDismissed.value = true;
  }

  function clearNetworkPoll(): void {
    if (!networkPollId) return;
    clearTimeout(networkPollId);
    networkPollId = null;
  }

  function clearLocationPoll(): void {
    if (!locationPollId) return;
    clearTimeout(locationPollId);
    locationPollId = null;
  }

  function scheduleNetworkPoll(): void {
    clearNetworkPoll();
    if (!started.value || isOnline.value) return;
    networkPollId = setTimeout(() => {
      networkPollId = null;
      void checkNetwork();
    }, NETWORK_POLL_MS);
  }

  function scheduleLocationPoll(): void {
    clearLocationPoll();
    if (!started.value || !isMobileRuntime.value || !hasSignedInUser.value) return;
    // Monitoraggio CONTINUO: veloce in recupero (GPS giù → ricontrolla spesso per
    // far sparire il banner appena torna), lento quando il GPS è attivo (per
    // rilevare lo spegnimento senza un fix GPS al secondo).
    const delay = gpsAvailable.value ? LOCATION_MONITOR_MS : LOCATION_POLL_MS;
    locationPollId = setTimeout(() => {
      locationPollId = null;
      void checkLocation();
    }, delay);
  }

  async function checkNetwork(): Promise<boolean> {
    if (checkingNetwork.value) return isOnline.value;

    checkingNetwork.value = true;
    const wasOnline = isOnline.value;
    try {
      const online = await hasInternetAccess();
      isOnline.value = online;
      if (!wasOnline && online) {
        emitWindowEvent(MOBILE_NETWORK_RESTORED_EVENT);
      }
      return online;
    } finally {
      checkingNetwork.value = false;
      scheduleNetworkPoll();
    }
  }

  let inFlightLocationCheck: Promise<boolean> | null = null;

  async function checkLocation(options: { requestPermission?: boolean } = {}): Promise<boolean> {
    if (!isMobileRuntime.value || !hasSignedInUser.value) {
      gpsAvailable.value = true;
      return true;
    }
    if (checkingLocation.value) {
      // Un controllo è già in volo (tipicamente il poll di sfondo che gira di
      // continuo). Per i controlli SENZA richiesta permessi — come quello
      // immediato lanciato all'ingresso in MapView per far comparire subito il
      // banner — non restituire lo stato STANTIO di gpsAvailable: quello
      // "perderebbe" il controllo immediato e il banner non comparirebbe finché
      // non scatta il poll successivo (fino a 15s quando il GPS risultava
      // attivo). Attendi invece l'esito del controllo già in corso, così il
      // chiamante vede lo stato GPS aggiornato (niente seconda probe).
      if (!options.requestPermission) {
        if (inFlightLocationCheck) {
          return await inFlightLocationCheck.catch(() => gpsAvailable.value);
        }
        return gpsAvailable.value;
      }
      // Il poll di sfondo gira di continuo: il TAP dell'utente sul refresh
      // (requestPermission) non va scartato — attende il check in corso e
      // riparte, così la richiesta permessi viene mostrata davvero.
      for (let attempt = 0; attempt < 3 && checkingLocation.value; attempt += 1) {
        await (inFlightLocationCheck ?? Promise.resolve()).catch(() => { });
      }
      if (checkingLocation.value) return gpsAvailable.value;
    }

    checkingLocation.value = true;
    const wasAvailable = gpsAvailable.value;
    const run = (async () => {
    // Permesso già negato in modo permanente: il dialogo di sistema non può
    // più comparire — il refresh porta l'utente alle impostazioni dell'app.
    const priorPermission = options.requestPermission
      ? await geolocationService.checkPermissions().catch(() => 'prompt')
      : null;
    try {
      const position = await geolocationService.probeCurrentPosition({
        requestPermission: options.requestPermission,
        timeout: 2200,
        maximumAge: 0,
      });
      gpsAvailable.value = true;

      if (!wasAvailable) {
        await geolocationService.saveMyLocation(
          position.coords.latitude,
          position.coords.longitude,
        );
        lastGpsSaveAt = Date.now();
        userStore.locationSource = 'gps';
        await userStore.fetchProfile().catch(() => { });
        emitWindowEvent(MOBILE_LOCATION_RESTORED_EVENT);
      } else if (Date.now() - lastGpsSaveAt >= LOCATION_KEEPALIVE_MS) {
        // Keep-alive: GPS già attivo e utente online — si ri-salva il fix così
        // il server vede la posizione GPS come "viva" e il fallback offline→
        // indirizzo non scatta mai per chi sta usando l'app. Nessun evento di
        // ripristino né refetch del profilo: non è un cambiamento di stato.
        // Il timestamp si aggiorna anche a salvataggio FALLITO: mantiene la
        // cadenza dei 10 minuti pure durante un'assenza di rete, invece di
        // ritentare la RPC a ogni probe (una ogni 15s → hot loop).
        lastGpsSaveAt = Date.now();
        await geolocationService.saveMyLocation(
          position.coords.latitude,
          position.coords.longitude,
        ).then(() => {
          userStore.locationSource = 'gps';
        }).catch(() => { /* transitorio: si ritenta al prossimo giro di keep-alive */ });
      }
      return true;
    } catch {
      gpsAvailable.value = false;
      if (options.requestPermission && priorPermission === 'denied') {
        const current = await geolocationService.checkPermissions().catch(() => 'denied');
        if (current === 'denied') {
          await geolocationService.openSettings();
        }
      }
      return false;
    } finally {
      checkingLocation.value = false;
      scheduleLocationPoll();
    }
    })();
    inFlightLocationCheck = run;
    try {
      return await run;
    } finally {
      if (inFlightLocationCheck === run) inFlightLocationCheck = null;
    }
  }

  function onOffline(): void {
    isOnline.value = false;
    scheduleNetworkPoll();
  }

  function onOnline(): void {
    void checkNetwork();
  }

  // Ritorno in foreground (anche dopo che l'utente ha toccato GPS/rete nelle
  // impostazioni di sistema e torna nell'app): ricontrolla subito, così il
  // banner appare/sparisce senza attendere il poll lento.
  function onForeground(): void {
    if (!started.value) return;
    void checkNetwork();
    void checkLocation();
  }

  function start(): void {
    if (started.value) return;
    started.value = true;
    if (typeof window !== 'undefined') {
      window.addEventListener('online', onOnline);
      window.addEventListener('offline', onOffline);
      window.addEventListener('swapwish:app-foreground', onForeground);
    }
    void checkNetwork();
    void checkLocation();
  }

  function stop(): void {
    started.value = false;
    clearNetworkPoll();
    clearLocationPoll();
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('swapwish:app-foreground', onForeground);
    }
  }

  watch(
    () => userStore.user?.id,
    () => {
      if (!started.value) return;
      void checkLocation();
    }
  );

  watch(
    () => userStore.hasDefaultAddress,
    (hasAddress, hadAddress) => {
      if (!started.value) return;
      if (hasAddress && !hadAddress) {
        emitWindowEvent(MOBILE_LOCATION_RESTORED_EVENT);
      }
      scheduleLocationPoll();
    }
  );

  return {
    isOnline,
    gpsAvailable,
    checkingNetwork,
    checkingLocation,
    showNetworkBanner,
    showLocationBanner,
    showLocationAddressBanner,
    dismissAddressBanner,
    start,
    stop,
    checkNetwork,
    checkLocation,
  };
});
